import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import { Flex } from '../box';
import Text from '../text';
import { colors, spacing } from '../../style';
import Checkbox, { CheckboxProps } from './checkbox';

export interface CheckboxLabelProps extends Omit<CheckboxProps, 'id'> {
  label?: React.ElementType | React.JSX.Element | string;
  subLabel?: React.ElementType | string;
  marginBottom?: keyof typeof spacing;
  value?: boolean;
  'data-testid'?: string;

  id: string | number;
  alignItems?: 'flex-start' | 'center' | 'flex-end';
  required?: boolean;
}

const StyledCheckboxLabel = styled(
  ({
    label,
    className,
    disabled,
    subLabel,
    marginBottom = 'none',
    id,
    checked,
    value,
    onChange,
    required,
    alignItems = 'flex-start',
    ...rest
  }: CheckboxLabelProps) => {
    const idAsString = id ? id.toString() : undefined;

    return (
      <Flex
        disabled={disabled}
        marginBottom={marginBottom}
        alignItems={alignItems}
        clickable={!disabled}
        className={classNames('box-checkbox-label', className, { disabled })}
      >
        <Checkbox
          id={idAsString}
          disabled={disabled}
          {...rest}
          className={'label-checkbox'}
          checked={checked || !!value}
          onChange={onChange}
          required={required}
        />
        <label
          htmlFor={idAsString}
          data-testid={'checkbox-label'}
          className={classNames('label-container', { disabled })}
        >
          <Text size={'md'} variant={disabled ? 'gray600' : 'inherit'} className="checkbox-label">
            {label}
          </Text>
          {subLabel && (
            <Text
              componentClass={'span'}
              marginBottom={'xs'}
              marginTop={'xs'}
              variant={disabled ? 'gray400' : 'gray600'}
              size={'md'}
            >
              {subLabel}
            </Text>
          )}
        </label>
      </Flex>
    );
  },
)`
  &:hover:not(.disabled) {
    .hover-overlay {
      background: ${colors.gray300};
    }
  }

  button[role='checkbox'] {
    position: relative;
    top: 1px;
  }

  .label-checkbox {
    position: relative;
    flex-shrink: 0;
  }

  .label-container {
    cursor: pointer;
    margin-left: 8px;
  }

  .label-container {
    &.disabled {
      > span {
        cursor: not-allowed;
      }
    }

    > span {
      display: block;
    }
  }
`;

const CheckboxLabel = (props: CheckboxLabelProps) => <StyledCheckboxLabel {...props} />;

export default CheckboxLabel;
